const DEFAULT_ERROR_MESSAGE = "Request failed";

export function isCanceledAPIError(error) {
    return error?.code === "ERR_CANCELED" || error?.__CANCEL__ === true ||
        error?.raw?.code === "ERR_CANCELED" || error?.raw?.__CANCEL__ === true;
}

function extractErrorData(error) {
    const data = error?.data ?? error?.raw?.data ?? error?.raw?.response?.data ?? error?.response?.data ?? null;
    // A Blob body (responseType:'blob' downloads, e.g. PDF/label exports) can't
    // be inspected synchronously here; return null so callers special-case it
    // instead of receiving an unparseable Blob (NL4).
    if (typeof Blob !== "undefined" && data instanceof Blob) return null;
    return data;
}

// NOTE: normalizeAPIError always returns a NEW object (even when the input was
// already normalized), so do not rely on reference identity (===) of the
// returned error (NL5). The `isApiError` flag lets the global unhandled-rejection
// handler tell an expected API error apart from an unexpected crash (F26).
export function normalizeAPIError(error) {
    if (error?.status && error?.message && Object.hasOwn(error, "raw")) {
        const data = extractErrorData(error);
        return {
            ...error,
            isApiError: true,
            data,
            details: error.details ?? data?.details ?? null,
            fieldErrors: error.fieldErrors ?? data?.fieldErrors ?? data?.field_errors ?? data?.errors ?? null,
        };
    }

    const response = error?.response;
    const data = response?.data;
    const status = response?.status ?? error?.status ?? 0;
    const code = data?.code ?? error?.code ?? (status ? `HTTP_${status}` : "NETWORK_ERROR");
    const fieldErrors = data?.fieldErrors ?? data?.field_errors ?? data?.errors ?? null;

    let message = data?.error ?? data?.message ?? error?.message;
    if (!message && response?.statusText) message = response.statusText;
    if (!message) message = DEFAULT_ERROR_MESSAGE;

    return {
        status,
        code,
        message,
        data,
        details: data?.details ?? null,
        fieldErrors,
        raw: error
    };
}

export async function normalizeFetchError(response, rawBody = null) {
    let data = rawBody;

    if (data == null) {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
            data = await response.json().catch(() => null);
        } else {
            data = await response.text().catch(() => null);
        }
    }

    const message =
        data?.error ||
        data?.message ||
        (typeof data === "string" && data.trim()) ||
        response.statusText ||
        DEFAULT_ERROR_MESSAGE;

    return {
        status: response.status,
        code: data?.code ?? `HTTP_${response.status}`,
        message,
        data,
        details: data?.details ?? null,
        fieldErrors: data?.fieldErrors ?? data?.field_errors ?? data?.errors ?? null,
        raw: { response, data }
    };
}

export function getAPIErrorStatus(error) {
    return normalizeAPIError(error).status;
}

export function getAPIErrorData(error) {
    return normalizeAPIError(error).data;
}

export function getAPIErrorDetails(error) {
    return normalizeAPIError(error).details;
}

export function getAPIErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
    const message = normalizeAPIError(error).message;
    return message && message !== DEFAULT_ERROR_MESSAGE ? message : fallback;
}

// These responses do not prove a write was rejected; it may still have completed.
export function isMutationOutcomeUnconfirmed(error) {
    const status = getAPIErrorStatus(error);
    return status < 400 || status === 408 || status >= 500 ||
        (status === 409 && /request in progress/i.test(getAPIErrorMessage(error, "")));
}

export function isAPIErrorStatus(error, status) {
    return getAPIErrorStatus(error) === status;
}

// Axios codes for "the request timed out" — our global 30s client timeout
// (apiConfig.js) reports ECONNABORTED, while some axios builds use ETIMEDOUT.
const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"];

// `code` survives normalizeAPIError (it copies error.code straight through), so
// these helpers work on both raw axios errors and normalized ones.
function errorCode(error) {
    return error?.code ?? error?.raw?.code ?? null;
}

function hasHTTPResponse(error) {
    return !!(error?.response ?? error?.raw?.response) || Boolean(error?.status);
}

export function isTimeoutError(error) {
    if (TIMEOUT_CODES.includes(errorCode(error))) return true;
    // Fallback: a timeout that lost its code still carries no response and a
    // "timeout of 30000ms exceeded"-style message.
    return !hasHTTPResponse(error) && /timeout/i.test(error?.message ?? "");
}

// True when the request never produced an HTTP response: no connection,
// DNS/CORS failure (ERR_NETWORK), or a client/OS timeout. Callers should show a
// connectivity message rather than the raw error.
export function isNetworkError(error) {
    return errorCode(error) === "ERR_NETWORK" || isTimeoutError(error);
}

// Friendly message for connectivity failures, or null so callers fall through
// to status-specific handling (401, etc.).
export function getConnectionErrorMessage(error) {
    if (isTimeoutError(error)) return "Connection timed out. Please try again.";
    if (isNetworkError(error)) return "No internet connection";
    return null;
}
